/**
 * 국세청 매입(전자세금)계산서 목록(.xls) 파싱 + 업로드 API.
 *
 * 홈택스에서 받은 '매입전자세금계산서목록' .xls 의 두 시트를 읽어
 * 백엔드(/purchases/nts-import)로 전송 → 대조/채움/삽입.
 *   - '세금계산서' 시트: 계산서 1건 = 1행 (작성일자/승인번호/공급자사업자번호/상호/공급가액 등)
 *   - '품목' 시트: 승인번호별 품목명(설명용)
 */

import * as XLSX from 'xlsx';

/** 국세청 계산서 '품목' 시트의 라인 1줄 (홈택스 원본 품목/규격/수량/금액) */
export interface NtsLineItem {
  name: string;          // 품목명
  spec: string;          // 규격
  qty: number | null;    // 수량
  unitPrice: number | null;  // 단가
  supply: number | null;     // 공급가액
  tax: number | null;        // 세액
}

export interface NtsRow {
  writeDate: string;     // 작성일자 YYYY-MM-DD
  approvalNo: string;    // 승인번호
  supplierBno: string;   // 공급자 사업자등록번호(숫자만)
  supplierName: string;  // 공급자 상호
  supply: number;        // 공급가액
  total: number;         // 합계금액
  taxAmount: number;     // 세액(부가세) — 세관 수입 부가세 기록용
  itemName: string;      // 품목명(품목시트 조인) → 매입 품목
  itemSpec: string;      // 규격(품목시트 조인) → 매입 규격
  items: NtsLineItem[];  // 품목 시트 라인 전체 (국세청 발행 내역 보관용)
  // 수동 매칭 — 미리보기에서 사용자가 합산 발행 건을 직접 묶은 경우
  manualPids?: number[];
  manualBellIds?: number[];
}

export interface NtsParseResult {
  rows: NtsRow[];
  companyId: 'SM' | 'EJ';   // 공급받는자(우리 사업자)
  buyerBno: string;
}

// 헬퍼들은 매출계산서 대조 파서(nts-sales-check-api)와 공용 — export.
export const digits = (s: unknown) => String(s ?? '').replace(/\D/g, '');
export const num = (s: unknown) => Math.round(Number(String(s ?? '').replace(/,/g, '')) || 0);
export const ymd = (s: unknown) => {
  const t = String(s ?? '').trim();
  const m = /(\d{4})[-./](\d{1,2})[-./](\d{1,2})/.exec(t);
  if (!m) return '';
  return `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
};

export function findHeaderRow(rows: unknown[][], must: string[]): number {
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const cells = (rows[i] || []).map((c) => String(c ?? '').replace(/\s/g, ''));
    if (must.every((m) => cells.some((c) => c.includes(m)))) return i;
  }
  return -1;
}
export const colIndex = (header: unknown[], name: string, from = 0) => {
  const target = name.replace(/\s/g, '');
  for (let i = from; i < header.length; i++) {
    if (String(header[i] ?? '').replace(/\s/g, '').includes(target)) return i;
  }
  return -1;
};

/** .xls/.xlsx File → 파싱 결과. 형식이 안 맞으면 throw. */
export async function parseNtsFile(file: File): Promise<NtsParseResult> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const taxName = wb.SheetNames.find((n) => n.includes('세금계산서')) ?? wb.SheetNames[0]!;
  const taxRows = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[taxName]!, { header: 1, raw: false, defval: '' });

  const hIdx = findHeaderRow(taxRows, ['작성일자', '승인번호', '공급가액']);
  if (hIdx < 0) throw new Error("국세청 매입계산서 형식이 아닙니다 ('작성일자/승인번호/공급가액' 머리글을 찾지 못함).");
  const h = taxRows[hIdx]!;
  const iWrite = colIndex(h, '작성일자');
  const iApp = colIndex(h, '승인번호');
  const iSupBno = colIndex(h, '공급자사업자등록번호');
  const iSupName = colIndex(h, '상호', iSupBno + 1);
  const iBuyerBno = colIndex(h, '공급받는자사업자등록번호');
  const iSupply = colIndex(h, '공급가액');
  const iTotal = colIndex(h, '합계금액');
  const iTax = colIndex(h, '세액');
  // 홈택스 '목록조회' 형식 — 별도 '품목' 시트 없이 본문(세금계산서) 시트 우측에 품목 컬럼이 붙는다.
  //   (품목명/품목규격/품목수량/품목단가/품목공급가액/품목세액). 계산서 1건당 대표 품목 1줄.
  const iItemName = colIndex(h, '품목명');
  const iItemSpec = colIndex(h, '품목규격');
  const iItemQty = colIndex(h, '품목수량');
  const iItemUnit = colIndex(h, '품목단가');
  const iItemSupply = colIndex(h, '품목공급가액');
  const iItemTax = colIndex(h, '품목세액');

  // 품목 시트 → 승인번호별 품목명 / 규격 (분리 수집 — 매입 '품목'·'규격' 칼럼에 각각 표시)
  //   + 라인 전체(품명/규격/수량/단가/공급가액/세액)를 itemsByApp 에 보관 → 국세청 발행 내역 표시용.
  const namesByApp = new Map<string, string[]>();
  const specsByApp = new Map<string, string[]>();
  const itemsByApp = new Map<string, NtsLineItem[]>();
  // 품목 라인 숫자 — 소수 보존(수량 0.5개월 등, 컬럼이 numeric(_,2)). 요약 시트의 num()(반올림)과 별개.
  const numOrNull = (v: unknown): number | null => {
    const s = String(v ?? '').trim();
    if (s === '') return null;
    const n = Number(s.replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  };
  const itemSheet = wb.SheetNames.find((n) => n.includes('품목'));
  if (itemSheet) {
    const itemRows = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[itemSheet]!, { header: 1, raw: false, defval: '' });
    const ih = findHeaderRow(itemRows, ['승인번호', '품목명']);
    if (ih >= 0) {
      const ihh = itemRows[ih]!;
      const ja = colIndex(ihh, '승인번호');
      const jn = colIndex(ihh, '품목명');
      const js = colIndex(ihh, '규격');
      const jq = colIndex(ihh, '수량');
      const ju = colIndex(ihh, '단가');
      const jv = colIndex(ihh, '공급가액');
      const jt = colIndex(ihh, '세액');
      for (let r = ih + 1; r < itemRows.length; r++) {
        const row = itemRows[r]!;
        const app = String(row[ja] ?? '').trim();
        if (!app) continue;
        const nm = String(row[jn] ?? '').trim();
        const spec = js >= 0 ? String(row[js] ?? '').trim() : '';
        if (nm) { if (!namesByApp.has(app)) namesByApp.set(app, []); namesByApp.get(app)!.push(nm); }
        if (spec) { if (!specsByApp.has(app)) specsByApp.set(app, []); specsByApp.get(app)!.push(spec); }
        // 품명·규격이 모두 비면 무의미한 줄 → 건너뜀
        if (!nm && !spec) continue;
        const line: NtsLineItem = {
          name: nm, spec,
          qty: jq >= 0 ? numOrNull(row[jq]) : null,
          unitPrice: ju >= 0 ? numOrNull(row[ju]) : null,
          supply: jv >= 0 ? numOrNull(row[jv]) : null,
          tax: jt >= 0 ? numOrNull(row[jt]) : null,
        };
        if (!itemsByApp.has(app)) itemsByApp.set(app, []);
        itemsByApp.get(app)!.push(line);
      }
    }
  }
  // 별도 '품목' 시트가 없거나 비었고, 본문에 품목명 컬럼이 있으면 본문에서 품목을 읽는다.
  const useMainSheetItems = itemsByApp.size === 0 && iItemName >= 0;

  let buyerBno = '';
  const rows: NtsRow[] = [];
  for (let r = hIdx + 1; r < taxRows.length; r++) {
    const row = taxRows[r]!;
    const approvalNo = String(row[iApp] ?? '').trim();
    const writeDate = ymd(row[iWrite]);
    if (!approvalNo || !writeDate) continue;
    if (!buyerBno && iBuyerBno >= 0) buyerBno = digits(row[iBuyerBno]);
    // 본문 시트 품목(홈택스 목록조회) — 이 행의 대표 품목을 승인번호별 맵에 적재.
    if (useMainSheetItems) {
      const nm = String(row[iItemName] ?? '').trim();
      const spec = iItemSpec >= 0 ? String(row[iItemSpec] ?? '').trim() : '';
      if (nm || spec) {
        if (nm) { if (!namesByApp.has(approvalNo)) namesByApp.set(approvalNo, []); namesByApp.get(approvalNo)!.push(nm); }
        if (spec) { if (!specsByApp.has(approvalNo)) specsByApp.set(approvalNo, []); specsByApp.get(approvalNo)!.push(spec); }
        const line: NtsLineItem = {
          name: nm, spec,
          qty: iItemQty >= 0 ? numOrNull(row[iItemQty]) : null,
          unitPrice: iItemUnit >= 0 ? numOrNull(row[iItemUnit]) : null,
          supply: iItemSupply >= 0 ? numOrNull(row[iItemSupply]) : null,
          tax: iItemTax >= 0 ? numOrNull(row[iItemTax]) : null,
        };
        if (!itemsByApp.has(approvalNo)) itemsByApp.set(approvalNo, []);
        itemsByApp.get(approvalNo)!.push(line);
      }
    }
    // 같은 계산서에 동일 품목명이 여러 줄(월정액 등)이면 중복 제거 후 최대 3개.
    const names = [...new Set(namesByApp.get(approvalNo) ?? [])];
    const specs = [...new Set(specsByApp.get(approvalNo) ?? [])];
    rows.push({
      writeDate,
      approvalNo,
      supplierBno: digits(row[iSupBno]),
      supplierName: String(row[iSupName] ?? '').trim(),
      supply: num(row[iSupply]),
      total: iTotal >= 0 ? num(row[iTotal]) : 0,
      taxAmount: iTax >= 0 ? num(row[iTax]) : 0,
      itemName: names.slice(0, 3).join(', ').slice(0, 280),
      itemSpec: specs.slice(0, 3).join(', ').slice(0, 480),
      items: (itemsByApp.get(approvalNo) ?? []).slice(0, 200),
    });
  }
  if (!rows.length) throw new Error('계산서 데이터 행을 찾지 못했습니다.');

  const companyId: 'SM' | 'EJ' = buyerBno === '2578602520' ? 'EJ' : 'SM';
  return { rows, companyId, buyerBno };
}

export interface NtsImportSummary {
  total: number; skipDone: number; matchedPurchase: number; matchedManual: number;
  filledDates: number; matchedBell: number; inserted: number;
  // 비상벨(현진) 계산서 자동매칭 실패 → 매입 등록 안 하고 수동 확인으로 분리 (배포 시차 호환 optional)
  bellReview?: number;
  // 자동 조합 매칭 건수 · 발행일 갱신(덮어씀) 건수 · 이미 처리분 발행일 정정 건수 (배포 시차 호환 optional)
  matchedCombo?: number; overwrittenDates?: number; reconciledDates?: number;
  // 수출입 라우팅 (배포 시차 호환을 위해 optional)
  importVat?: number; importFreight?: number;
  // 원 발행 ↔ 마이너스 취소 짝(줄 수 · v2.306.1 · 옛 서버는 없음)
  cancelPair?: number;
  // 신규 등록 중 판관비 자동 확정(확인필요 없이 · v2.311.0 · 개발요청 #24 · 옛 서버는 없음)
  insertedSga?: number;
}
/** 귀속 (년, 월) — 하도급 매입 maint_year · maint_month 와 같은 자 (v2.309.0) */
export interface NtsServiceMonth { y: number; m: number }

export interface NtsImportRowResult {
  approvalNo: string; writeDate: string; supplierName: string; supply: number;
  status: 'skip_done' | 'matched_purchase' | 'matched_manual' | 'matched_bell' | 'bell_review' | 'insert' | 'insert_sga' | 'import_vat' | 'import_freight' | 'cancel_pair';
  detail: string;
  /** 서버가 계산서 품목·규격에서 읽은 귀속 월(「8월분」 등) — 없으면 null (v2.309.0 · 옛 서버는 안 준다) */
  serviceMonth?: NtsServiceMonth | null;
}
export interface NtsImportResult {
  applied: boolean;
  summary: NtsImportSummary;
  rows: NtsImportRowResult[];
}

export async function ntsImport(
  rows: NtsRow[], companyId: 'SM' | 'EJ', apply: boolean, fileName?: string,
): Promise<NtsImportResult> {
  const res = await fetch('/api/purchases/nts-import', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    // fileName — 업로드 기록에만 쓴다(v2.239.0). 미리보기(apply=false)는 기록하지 않는다
    body: JSON.stringify({ rows, companyId, apply, fileName }),
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '업로드 실패');
  return json.data as NtsImportResult;
}

// ── 업로드 기록 (v2.239.0) ─────────────────────────────────────

/** 업로드 한 번([적용] 한 번) — 시각은 서버가 만든 한국 시각 글자 'YYYY-MM-DD HH:MM' */
export interface NtsUploadLogRow {
  id: number;
  companyId: 'SM' | 'EJ';
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedName: string | null;
  fileName: string | null;
  /** 파일의 계산서 장수 — 되살린 기록은 null */
  rowCount: number | null;
  minWriteDate: string | null;
  maxWriteDate: string | null;
  /** 이 기록 표가 생기기 전 업로드를 국세청 품목 내역 저장 시각으로 되살린 줄 */
  restored: boolean;
}

export interface NtsUploadLog {
  /** 사업자별 마지막 업로드 + 반영된 자료가 언제 것까지인가(작성일자 최댓값) */
  companies: Record<'SM' | 'EJ', { last: NtsUploadLogRow | null; dataUntil: string | null }>;
  /** 최근 12건 (최신순) */
  rows: NtsUploadLogRow[];
}

export async function getNtsUploadLog(): Promise<NtsUploadLog> {
  const res = await fetch('/api/purchases/nts-import/log', { credentials: 'include' });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '업로드 기록을 불러오지 못했습니다');
  return json.data as NtsUploadLog;
}

// ── 수동 매칭 (합산 발행 대응) ─────────────────────────────────

/** 매칭 후보 — 그 거래처의 포털 매입(승인번호 미기록분) */
export interface NtsCandPurchase {
  purchaseId: number;
  parentPurchaseId: number | null;
  supplier: string | null;
  description: string | null;
  itemName: string | null;
  spec: string | null;
  orderQty: number;
  amount: number;
  orderDate: string | null;
  invoiceDate: string | null;
  salesId: string | null;
  /** 계산서 매칭 바꾸기 모드 — 지금 이 계산서에 묶인 매입 (v2.307.0) */
  current?: boolean;
  /** 귀속 (년, 월) — 하도급 월별 매입은 줄마다 금액·발주일이 같아 이것으로만 갈린다 (v2.309.0 · v2.309.1 부터 하도급 줄만) */
  maintYear?: number | null;
  maintMonth?: number | null;
  /** 하도급 줄의 귀속 글자 — 「2026년 8월분」·「2026년 3분기분」·「2026년 연1회분」 (하도급 아니면 null · v2.309.1) */
  svcLabel?: string | null;
  /** 계산서 품목에 적힌 귀속 월이 이 줄의 기간 안 — 서버 자동 매칭과 같은 판정 (v2.309.1) */
  svcHit?: boolean;
  /** 하도급 줄의 건명 — 같은 금액·같은 품목의 계약 둘을 가른다 (v2.309.1) */
  projectName?: string | null;
}
/** 매칭 후보 — 비상벨 건별 매입 (현진아이씨티 계산서일 때만) */
export interface NtsCandBell {
  bellId: number;
  endUser: string | null;
  siteName: string | null;
  deviceCount: number;
  amount: number;
  invoiceDate: string | null;
  revenueYear: number;
  revenueMonth: number;
  current?: boolean;
}
/** 계산서 매칭 바꾸기 모드(?approvalNo=)에서만 오는 정보 (v2.307.0) */
export interface NtsRematchInfo {
  approvalNo: string;
  writeDate: string | null;
  supplier: string | null;
  /** 지금 묶인 합(국세청이 만든 줄만 있으면 그 금액 · 0원 기록이면 원래 공급가) */
  supply: number;
  currentPids: number[];
  currentBellIds: number[];
  /** 국세청이 만든 줄 — 다른 매입을 고르면 지워진다 */
  placeholders: { purchaseId: number; amount: number; category: string | null; reviewStatus: string | null }[];
  /** 여기서 못 바꾸는 까닭(취소 짝 · 수출입 비용) */
  blocked: string | null;
}
export interface NtsMatchCandidates {
  target?: { purchaseId: number; approvalNo: string; writeDate: string | null; supply: number; supplier: string | null };
  supplierNames: string[];
  isBellVendor: boolean;
  purchases: NtsCandPurchase[];
  bells: NtsCandBell[];
  rematch?: NtsRematchInfo;
  /** 계산서 품목·규격에 적힌 귀속 월 — 하도급 후보 중 그 달 줄을 표시·위로 (v2.309.0 · 옛 서버는 안 준다) */
  serviceMonth?: NtsServiceMonth | null;
}

/** 매칭 후보 조회 — purchaseId(확인필요 건) · bno/name(업로드 미리보기) · approvalNo(이미 매칭된 계산서 바꾸기) */
export async function fetchNtsMatchCandidates(
  params: { purchaseId: number } | { bno: string; name: string; serviceMonth?: NtsServiceMonth | null } | { approvalNo: string },
): Promise<NtsMatchCandidates> {
  const qs = new URLSearchParams();
  if ('purchaseId' in params) qs.set('purchaseId', String(params.purchaseId));
  else if ('approvalNo' in params) qs.set('approvalNo', params.approvalNo);
  else {
    if (params.bno) qs.set('bno', params.bno);
    if (params.name) qs.set('name', params.name);
    // 업로드 미리보기가 서버에서 받은 귀속 월을 그대로 돌려준다(판정은 서버 한 곳 — serviceMonthOf)
    if (params.serviceMonth) { qs.set('svcY', String(params.serviceMonth.y)); qs.set('svcM', String(params.serviceMonth.m)); }
  }
  const res = await fetch(`/api/purchases/nts-match-candidates?${qs.toString()}`, { credentials: 'include' });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '후보 조회 실패');
  return json.data as NtsMatchCandidates;
}

export interface NtsRematchResult {
  deletedId: number;
  matchedPurchases: number;
  matchedBells: number;
  filledPurchaseDates: number;
  filledBellDates: number;
}

/** 확인필요 자동등록 건 → 선택한 기존 매입/비상벨로 매칭 (자동등록 건은 삭제) */
export async function ntsRematch(
  purchaseId: number, pids: number[], bellIds: number[],
): Promise<NtsRematchResult> {
  const res = await fetch(`/api/purchases/${purchaseId}/nts-rematch`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pids, bellIds }),
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '매칭 실패');
  return json.data as NtsRematchResult;
}

export interface NtsInvoiceRematchResult {
  approvalNo: string;
  attached: number;
  detached: number;
  kept: number;
  /** 뺀 매입 중 발행일을 붙기 전 값으로 되돌린 수 */
  restoredDates: number;
  /** 지운 「국세청이 만든 줄」 */
  deletedPlaceholderIds: number[];
  /** 다 빼서 새로 만든 확인필요 줄 */
  createdReviewId: number | null;
}

/** 🔗 이미 매칭된 계산서 — 매입 다시 고르기 (v2.307.0). expect = 창을 열 때 묶여 있던 것(그 사이 바뀌면 409) */
export async function ntsInvoiceRematch(body: {
  approvalNo: string; pids: number[]; bellIds: number[]; expectPids: number[]; expectBellIds: number[];
}): Promise<NtsInvoiceRematchResult> {
  const res = await fetch('/api/purchases/nts-invoice-rematch', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '매칭 바꾸기 실패');
  return json.data as NtsInvoiceRematchResult;
}

/** 매칭 바꾸기 끝 안내 — 매입 창·계산서 묶음 창 공용 */
export function invoiceRematchSummary(r: NtsInvoiceRematchResult): string {
  const lines = [`계산서 매칭을 바꿨습니다 — 새로 붙음 ${r.attached}건 · 빠짐 ${r.detached}건 · 그대로 ${r.kept}건.`];
  if (r.restoredDates) lines.push(`빠진 매입 ${r.restoredDates}건은 발행일을 이 계산서가 붙기 전 값으로 되돌렸습니다.`);
  if (r.deletedPlaceholderIds.length) {
    lines.push(`국세청이 만든 줄 #${r.deletedPlaceholderIds.join(', #')} 은 지웠습니다(같은 금액이 두 번 잡히지 않게).`);
  }
  if (r.createdReviewId) {
    lines.push(`계산서가 어느 매입에도 안 붙어 「확인필요」 줄 #${r.createdReviewId} 를 만들었습니다 — 매입 목록 「확인필요」에서 열어 [🔗 기존 매입과 매칭]으로 맞는 매입에 붙이세요.`);
  }
  return lines.join('\n\n');
}

/**
 * 🧾 이미 들어간 계산서 — 확인필요 자동등록 건을 0원·기타로 남긴다 (v2.306.0).
 *   이 계산서 품목이 이미 다른 매입에 다 있을 때(같은 날 원 발행·마이너스·재발행). 승인번호는 남아 다음 업로드에 다시 안 생긴다.
 */
export async function ntsSettleZero(purchaseId: number, reason = ''): Promise<{ id: number; originalAmount: number; approvalNo: string }> {
  const res = await fetch(`/api/purchases/${purchaseId}/nts-settle-zero`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '0원 기록 실패');
  return json.data as { id: number; originalAmount: number; approvalNo: string };
}

// ── 계산서 묶음 모아보기 (같은 승인번호 = 계산서 1장) ─────────────

export interface NtsGroupPurchase {
  purchaseId: number;
  supplier: string | null;
  description: string | null;
  itemName: string | null;
  orderQty: number;
  amount: number | null;        // viewer 마스킹 시 null
  orderDate: string | null;
  invoiceDate: string | null;   // viewer 마스킹 시 null
  salesId: string | null;
}
export interface NtsGroupBell {
  bellId: number;
  endUser: string | null;
  siteName: string | null;
  deviceCount: number;
  amount: number | null;        // viewer 마스킹 시 null
  invoiceDate: string | null;   // viewer 마스킹 시 null
  revenueYear: number;
  revenueMonth: number;
}
/** 국세청 발행 내역 라인 (nts_invoice_item — 홈택스 원본 품목/규격/수량/금액) */
export interface NtsInvoiceItemRow {
  seq: number;
  itemName: string | null;
  spec: string | null;
  qty: number | null;
  unitPrice: number | null;   // viewer 마스킹 시 null
  supply: number | null;      // viewer 마스킹 시 null
  tax: number | null;         // viewer 마스킹 시 null
}
export interface NtsGroupResult {
  approvalNo: string;
  supplierName: string | null;   // 국세청 발행 공급자(거래처명)
  writeDate: string | null;      // 국세청 작성일자
  ntsItems: NtsInvoiceItemRow[]; // 국세청 발행 품목 내역 (없으면 빈 배열)
  purchases: NtsGroupPurchase[];
  bells: NtsGroupBell[];
}

/** 같은 승인번호(계산서 1장)로 묶인 매입/비상벨 조회 — 매입 목록 계산서번호 클릭 */
export async function fetchNtsGroup(approvalNo: string): Promise<NtsGroupResult> {
  const res = await fetch(`/api/purchases/nts-group?approvalNo=${encodeURIComponent(approvalNo)}`, {
    credentials: 'include',
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '계산서 묶음 조회 실패');
  return json.data as NtsGroupResult;
}

// ── 국세청 매칭 점검 (의심 매칭 발견) ─────────────────────────────
export interface NtsCheckPurchase {
  purchaseId: number;
  supplier: string | null;
  amount: number;
  orderDate: string | null;
  invoiceDate: string | null;
  ntsApprovalNo: string;
  ntsWriteDate: string | null;   // 승인번호 안의 작성일자(국세청 발행일)
  gapDays: number | null;        // 주문일↔발행일 (일)
  reasons: string[];
}
export interface NtsCheckBell {
  bellId: number;
  siteName: string | null;
  endUser: string | null;
  amount: number;
  invoiceDate: string | null;
  ntsApprovalNo: string;
  ntsWriteDate: string | null;
  reasons: string[];
}
export interface NtsCheckResult {
  purchases: NtsCheckPurchase[];
  bells: NtsCheckBell[];
}

/** 국세청 매칭 점검 — 발행일이 안 맞거나 주문일과 한참 먼, 의심되는 매칭 목록 */
export async function fetchNtsCheck(): Promise<NtsCheckResult> {
  const res = await fetch('/api/purchases/nts-check', { credentials: 'include' });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '점검 조회 실패');
  return json.data as NtsCheckResult;
}
